/**
 * Help center content organized by section.
 * Each article can optionally link to a guided tour.
 */

export const helpSections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'RocketLaunch',
    articles: [
      {
        title: 'Platform Overview',
        tourId: 'welcome',
        content: `Evolunic EIS is an AI-powered engineering intelligence platform for managing CAD, requirements, compliance, and documents across your projects.

**Workspace:**
- **Projects** \u2014 Create and manage engineering projects with version control (branches, merges, releases), milestones, activity feed, and decision journal

**Analytics:**
- **Analytics** \u2014 Standalone section with 4 tabs: Project Brief (scorecard, KPIs, milestones), Intelligence (week-over-week trends), Risks & Attention (flagged items), AI Health Summary

**Engineering Lifecycle:**
- **Requirements** \u2014 Source of truth. 6 tabs: Requirements list/tree, Import & Extract (AI), Export, V&V Matrix, Change Requests (ECRs/ECOs), Risk Register
- **CAD** \u2014 Import, view, and generate 2D/3D CAD files with integrated viewer, AI drawing agent, and BOM extraction
- **Compliance** \u2014 4 tabs: Standards management, Traceability Matrix, Dashboard (per-drawing verdicts), Audit Log
- **Verification** \u2014 AI-powered V&V. 4 tabs: Design Gates, Test Management (AI-generated test cases), Inspection Checklists (AI-generated from CAD), Verification Matrix (auto-mapped coverage)
- **Documents** \u2014 4 tabs: Document list with detail drawer, AI Report Generation, Side-by-Side comparison, Linking (entity relationships)

**Utilities:**
- **Orchestrator** \u2014 Visual pipeline builder connecting engineering tools (Preview)
- **Knowledge Base** \u2014 Pin documents, CAD files, requirements, and standards as permanent e-pilot context. Recently Deleted tab for recovery.

**AI Assistant:**
- **e-pilot** \u2014 49 tools for requirements, CAD, compliance, analytics, milestones, decisions, and more. Attach files for context. Knowledge Base docs searched automatically.`,
      },
      {
        title: 'Creating Your First Project',
        content: `1. Navigate to **Projects** in the sidebar
2. Click **New Project**
3. Enter a project name, description, and assign team members
4. The project starts with a **main** branch
5. Create feature branches for parallel work, then merge back to main`,
      },
      {
        title: 'Engineering Domain \u2014 The Most Important Setting',
        content: `**Setting the Engineering Domain is critical** \u2014 it controls how the entire platform understands your project.

Go to **Project Home** \u2192 **General** \u2192 **Engineering Domain** dropdown.

**Available domains:** Railway, Optoelectronics (more coming soon)

**What the domain controls:**

| Feature | How domain affects it |
|---|---|
| **CAD Generation** | AI uses domain-specific components, geometry, standards, and assembly templates. A railway project generates rail profiles, turnouts, fastenings. An optoelectronics project generates laser assemblies, fiber layouts, optical paths. |
| **BOM Extraction** | AI identifies parts using a domain-specific knowledge base (185 railway components, 79 optoelectronics components). Without domain set, BOM extraction uses generic visual analysis with low accuracy. |
| **e-pilot Context** | Domain knowledge is available to e-pilot for answering domain-specific questions about standards, materials, and design rules. |

**Example:** A railway turnout drawing with domain set to "Railway" correctly identifies switch blades (lan\u00e7a curva/recta), baseplates (Chapim PI140-13), SKL12 clips, and timber sleepers with correct materials and EN standards. Without the domain set, the same drawing might be misidentified as a conveyor system.

**Always set the domain before uploading CAD files or running BOM extraction.**`,
      },
      {
        title: 'Switching Projects & Branches',
        content: `Use the **Project Switcher** in the header toolbar to:
- Select a different project from the dropdown
- Switch between branches within the current project
- Access your 5 most recent projects quickly

All views (CAD, Documents, Requirements, etc.) automatically filter to the selected project and branch.`,
      },
    ],
  },
  {
    id: 'cad',
    title: 'Design / CAD',
    icon: 'Construction',
    articles: [
      {
        title: 'Importing CAD Files',
        tourId: 'cad',
        content: `1. Navigate to **Design / CAD**
2. Click the **Import** button in the file list sidebar
3. Select your file (.dwg, .dxf, .step, .prt, .asm, .jpg, .png)
4. Choose the **Source System** from the dropdown:
   - SolidWorks, AutoCAD, CATIA, NX / Siemens, Autodesk Inventor, Revit, Creo / Pro-E, Solid Edge, Rhino, SketchUp, STEP / IGES (Neutral), or Other
5. Add a version number and description
6. Click **Import** \u2014 the file uploads and appears in the file list

The source system is saved as metadata for tracking and organization. All formats are displayed through the same integrated viewer regardless of origin.`,
      },
      {
        title: 'Using the CAD Viewer',
        content: `The integrated CAD Viewer displays 2D drawings and 3D models from any supported source system:

- **Select a drawing** from the dropdown in the viewer toolbar
- **Fit to View** \u2014 resets the camera to show the full model
- **Fullscreen** \u2014 expands the viewer (e-pilot stays visible)
- **Download** \u2014 downloads the source file (STEP, DWG, DXF)`,
      },
      {
        title: 'Viewer Tools',
        content: `The viewer toolbar includes specialized tools:

- **Measure** \u2014 Click two points to measure distance, or measure angles and areas
- **Markup** \u2014 Draw annotations, text, arrows, and shapes for design reviews
- **Section Plane** \u2014 Cut through 3D models to see internal structure

Click a tool to activate it (turns purple). Click again to deactivate.`,
      },
      {
        title: 'AI-Generated Drawings',
        content: `Use e-pilot in CAD mode to generate drawings:

1. Enable **CAD mode** in the chat panel (or it auto-enables on the Design / CAD view)
2. Describe what you want: "Draw a UIC60 rail cross-section profile" or "Draw a steel baseplate 500x300mm with 4 bolt holes"
3. The AI Drawing Agent generates a 2D CAD drawing with proper layers, dimensions, and hatching
4. The generated drawing appears in the viewer and file list

For 3D models, say "3D" explicitly (e.g., "Create a 3D flange model").

The Drawing Agent uses an engineering knowledge base with railway components (rail profiles, fastenings, sleepers), design rules, and assembly templates for dimensionally accurate output.`,
      },
      {
        title: 'BOM Extraction',
        content: `Extract a Bill of Materials from any CAD drawing using AI:

1. Open a drawing in the **CAD Viewer**
2. Click the **BOM icon** (table icon) in the viewer toolbar
3. The system extracts structured data from the DWG file and analyzes the drawing image with AI
4. A **BOM Review Dialog** opens with the extracted parts list

The extraction uses a dual approach for maximum accuracy:
- **Structured extraction** \u2014 reads tables, block attributes, and text entities directly from the DWG file
- **Visual analysis** \u2014 AI analyzes the rendered drawing image to identify components

Each row shows a **confidence score** (green = high, yellow = medium, red = low) indicating extraction reliability.`,
      },
      {
        title: 'Reviewing & Editing BOM',
        content: `After extraction, the BOM Review Dialog shows all extracted items in an editable table:

- **Click any cell** to edit its value inline (Part Number, Description, Qty, Material, Weight, Standards, etc.)
- Edited cells are marked with a **green checkmark** (verified by user) and set to 100% confidence
- **Low-confidence rows** (yellow/red) are highlighted \u2014 review these first
- The **Title Block** section shows drawing metadata (drawing number, title, material, revision)

**Exporting:**
- Click **Export Excel** or **Export CSV** to download the BOM as a spreadsheet
- Click **Save** to store the reviewed BOM back to the drawing record

You can also extract BOM via chat: ask e-pilot in CAD mode "Extract BOM from this drawing" and it will run the same extraction pipeline.`,
      },
    ],
  },
  {
    id: 'requirements',
    title: 'Requirements',
    icon: 'Assignment',
    articles: [
      {
        title: 'Tracking Requirements',
        tourId: 'requirements',
        content: `1. Navigate to **Requirements**
2. Click **Add Requirement** to create a new requirement
3. Enter a requirement ID (auto-prefixed with "REQ-") and description
4. Set the verification method: Test, Analysis, Inspection, or Demonstration
5. Set the status: Proposed, Open, In Progress, Verified, or Failed
6. Optionally attach a document (PDF, Word, Excel, images)

**Filtering & search:**
- Filter by status (All, Open, In Progress, Verified, Failed)
- Search by requirement ID or description

**Attachments:**
- Requirements with attached files show a download link in the Attachment column
- Click the filename to download the attached document
- Supported formats: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), text, and images`,
      },
      {
        title: 'Document Analysis & AI Extraction',
        content: `The **Document Analysis** tab uses AI to analyze requirement documents and extract structured data:

1. Switch to the **Document Analysis** tab in the Requirements view
2. Select a requirement that has an attached document
3. Choose an analysis type:
   - **Summary** \u2014 Overview of document content
   - **Requirements** \u2014 AI extracts individual requirements from the document and lets you import them
   - **Technical** \u2014 Detailed technical analysis of engineering content
   - **Full Analysis** \u2014 Comprehensive analysis combining all of the above
4. Select the response language (20+ languages supported)
5. Click **Analyze Document** (or **Extract Requirements** when Requirements type is selected)

**AI Requirements Extraction (Requirements type):**
When you select the **Requirements** analysis type, the system runs a 3-stage extraction workflow:
1. **Extract** \u2014 AI analyzes the attached document and identifies individual requirements with descriptions
2. **Review** \u2014 Extracted requirements appear in an editable table. You can modify descriptions, toggle selection with checkboxes, and review confidence levels
3. **Import** \u2014 Click **Import Selected** to bulk-create the selected requirements in your project. They appear in the Requirements table with auto-generated IDs

**Other analysis types:**
- **Analysis sections** are shown as expandable accordions you can browse through
- Click **Download as DOCX** to export the full analysis as a formatted Word document with tables, metadata, and all sections`,
      },
    ],
  },
  {
    id: 'documents',
    title: 'Documents',
    icon: 'Description',
    articles: [
      {
        title: 'Document Management',
        tourId: 'documents',
        content: `1. Navigate to **Documents**
2. Browse documents by category (Specifications, Reports, Drawings, etc.)
3. Filter by type or search by title/ID
4. Click **Upload** to add a new document
5. Documents are version-controlled \u2014 upload new versions to track changes

**Clicking on a document** opens a detail drawer with four tabs:

- **Overview** \u2014 File info (created by, last updated, type), description, latest change summary, and linked ECOs
- **History** \u2014 Full version history showing every revision with dates and authors
- **Compare** \u2014 Select two versions to run a side-by-side diff analysis with severity levels (minor, moderate, major, critical)
- **Reports** \u2014 View all AI-generated reports linked to this document, expand to read sections, or download as a file

From the drawer you can also upload a new version or edit metadata directly.`,
      },
      {
        title: 'AI Report Generation',
        content: `The **AI Report Generation** tab lets you generate technical documents from your project files:

1. Switch to the **AI Report Generation** tab in the Documents view
2. **Select a source file** from the list (requirements or documents)
3. **Choose a report type:**
   - **Project Quality Plan (PQP)** \u2014 Quality control plan with checkpoints and acceptance criteria
   - **User Manual** \u2014 Assembly instructions, specifications, and maintenance procedures
   - **Verification Sheet** \u2014 Test procedures and acceptance criteria for validation
4. Optionally select a template
5. Click **Generate Document** \u2014 the AI analyzes your source file and generates a structured report

**Reviewing generated reports:**
- The report opens in a review panel with expandable sections
- Edit any section inline if adjustments are needed
- Switch to the **Metadata** tab to see generation details (source file, model used, timestamp)
- **Approve & Create Document** to finalize, or **Reject** to discard
- Approved reports appear in the Reports section and are linked to the source document`,
      },
    ],
  },
  {
    id: 'version-control',
    title: 'Version Control & Branches',
    icon: 'AccountTree',
    articles: [
      {
        title: 'Why Version Control?',
        content: `If you have ever managed files by saving copies like "Motor_Assembly_v2_FINAL.step" or "Drawing_RevB_John_edits.dwg", you know how quickly things get out of hand. Version control solves this.

**Think of it like a PDM vault** (SolidWorks PDM, Windchill, Teamcenter):
- Every file change is tracked automatically with a timestamp and author
- You can always go back to any previous version
- Multiple engineers can work on the same project without overwriting each other's work
- There is a single source of truth \u2014 the **main** branch

**Key difference from traditional folders:**
- No more renaming files with version numbers
- No emailing files back and forth
- No guessing which version is the latest
- Full audit trail of every change`,
      },
      {
        title: 'Understanding Branches',
        content: `A **branch** is like a personal workspace where you can make changes without affecting the released design.

**The main branch:**
- This is your **released baseline** \u2014 the official, approved state of the project
- You cannot upload files directly to main (it is protected)
- Changes only reach main through a **merge** (like a formal release process)

**Feature branches:**
- Create a branch when you want to work on something (e.g., "cooling-system-redesign")
- Your branch starts as a copy of main, so you have all existing files
- Upload new files, modify designs, experiment freely \u2014 nothing affects main
- When your work is ready, merge it back to main

**Analogy:** Think of main as the "Released" state in a PDM vault. A feature branch is like checking out files to your local workspace \u2014 you can make changes without affecting what other engineers see until you are ready to check them back in.

**How to create a branch:**
1. Go to **Projects** \u2192 **Branch Management** tab
2. Click **New Branch**
3. Enter a descriptive name (e.g., "motor-redesign" or "pcb-layout-v2")
4. The branch is created from your currently active branch`,
      },
      {
        title: 'Uploading & Staging Files',
        content: `When you upload a file to your branch, it enters a **staging area** (called "Pending Changes").

**What is staging?**
- Staging is like placing files in an outbox before formally submitting them
- Staged files appear in the **Pending Changes** panel at the top of the file list
- You can review what you are about to save before making it permanent
- You can **unstage** (undo) a file if you change your mind

**How to upload and stage files:**
1. Make sure you are on a **feature branch** (not main)
2. Click **Upload** or **Import** in the file list, or **drag and drop** files directly
3. Select your file and fill in the description
4. The file appears in **Pending Changes** with a badge showing "Added" or "Modified"

**Parallel uploads & background processing:**
- You can drop **multiple files at once** \u2014 they upload in parallel (up to 3 at a time)
- A progress panel appears in the bottom-right showing each file's status
- After upload completes, **extraction happens in the background** (text extraction, indexing, cloud storage)
- You can continue working while extraction runs \u2014 no need to wait
- Progress stages: Uploading \u2192 Uploading to cloud \u2192 Extracting text \u2192 Creating index \u2192 KB ingestion \u2192 Done

**The Pending Changes panel shows:**
- Which files are staged (with action: Added, Modified, or Deleted)
- Who staged each file
- An **Unstage** button to remove a file from the staging area
- A **Commit** button to save all staged changes

**Analogy:** Staging is like the "Check In" queue in SolidWorks PDM \u2014 you prepare your changes, review them, and then submit them all at once.`,
      },
      {
        title: 'Committing Changes',
        content: `A **commit** saves a permanent snapshot of all your staged changes. Think of it like creating a new revision (Rev A \u2192 Rev B).

**What happens when you commit:**
- All staged files are saved as a group with a single description
- A permanent record is created with: who committed, when, and what changed
- The commit becomes part of the branch's history
- Pending Changes is cleared (ready for your next batch of work)

**How to commit:**
1. Review your staged files in the **Pending Changes** panel
2. Click **Commit**
3. Write a clear commit message describing what changed and why
   - Good: "Updated motor housing dimensions per stress analysis results"
   - Bad: "changes" or "update"
4. Click **Commit** to save

**After committing:**
- Your files are safely recorded on the branch
- The panel shows "Ready to merge" \u2014 meaning your changes can now be merged to main
- You can continue working and make more commits before merging

**Analogy:** A commit is like incrementing the revision in PDM. Rev A \u2192 Rev B \u2192 Rev C. Each revision has a description and a timestamp, and you can always go back to a previous revision.`,
      },
      {
        title: 'Merging Branches',
        content: `**Merging** combines the changes from your feature branch back into main (or another target branch). This is like promoting your work from a workspace to the released vault.

**Step-by-step merge process:**
1. Go to **Branch Management** and click **Merge Branches**
2. Select the **source branch** (your feature branch with changes)
3. Select the **target branch** (usually main)
4. Click **Preview Merge** \u2014 the system analyzes both branches and shows:
   - Files added (new files from your branch)
   - Files modified (files you changed)
   - Files removed (files you deleted)
   - Conflicts (files changed on both branches \u2014 see below)
5. If there are no conflicts, click **Merge** to complete

**What are conflicts?**
- A conflict happens when the **same file** was modified on both branches
- Example: You updated "Motor_Assembly.step" on your branch, but someone else also updated it on main
- The system flags this and asks you to choose: keep your version, keep the other version, or upload a manually resolved file
- All conflict resolutions are recorded for traceability

**After merging:**
- Your changes are now part of main (the released baseline)
- The merge is recorded in the project activity log
- You can delete the feature branch if the work is complete

**Analogy:** Merging is like the release process in a PLM system. Your working changes are reviewed and promoted to the official released state.`,
      },
      {
        title: 'Project Activity Log',
        content: `Every action on the platform is automatically logged for full traceability.

**What is tracked:**
- File uploads and modifications
- Commits (who saved what, when, and why)
- Branch creation and deletion
- Merges (which branches were merged, by whom)
- Project creation and updates

**How to view activity:**
1. Go to **Projects** and select a project
2. On the **Home** tab, scroll to the **Project Activity** section
3. Browse the timeline of all actions
4. Use filters to narrow down by user, action type, or date

**Why this matters for engineers:**
- Full audit trail for quality management (ISO 9001, AS9100, etc.)
- Know exactly who changed a drawing and when
- Trace any file back to the commit that introduced it
- Required for design reviews and regulatory compliance

**Analogy:** This is the same audit trail you would find in Windchill, Teamcenter, or any PLM system \u2014 but automatically captured for every action.`,
      },
      {
        title: 'Project Milestones',
        content: `Milestones let you define key deliverables and track progress toward project goals.

**Creating milestones:**
1. Go to **Projects** and select a project
2. On the **Home** tab, scroll down to the **Milestones** panel (below Branch Management)
3. Click **Add Milestone**
4. Enter a title, optional description, target date, and status
5. Click **Create**

**Milestone statuses:**
- **Planned** \u2014 Not started yet
- **In Progress** \u2014 Work has begun (auto-set when progress > 0%)
- **Completed** \u2014 Finished (auto-set when progress reaches 100%)
- **Missed** \u2014 Target date passed without completion

**Updating progress via commits:**
When you commit changes to a branch, the commit dialog shows a **Link to Milestone** dropdown. Select a milestone and use the **progress slider** to indicate how much of the milestone this commit completes (e.g., move from 30% to 55%). The milestone status updates automatically:
- Progress goes above 0% \u2192 status becomes "In Progress"
- Progress reaches 100% \u2192 status becomes "Completed" with today\u2019s date

**Quick complete:**
Click the circle icon next to any milestone in the list to instantly mark it as completed (100%).

**Milestones + e-pilot:**
Your project objectives and milestone status are automatically shared with e-pilot. Ask it "What are the project milestones?" or "How much progress have we made?" and it will answer based on your current data.`,
      },
      {
        title: 'Project Objectives',
        content: `Project objectives provide free-text context about the project\u2019s goals, scope, and standards. This text is shared with e-pilot so the AI assistant understands your project.

**Setting objectives:**
1. Go to **Projects** and select a project
2. Click **Settings** (gear icon)
3. Fill in the **Project Objectives & Context** field
4. Click **Save Changes**

**Suggested format:**
\`\`\`
Goal: Design and validate a high-speed rail track section
Scope: Track geometry, rail profiles, fastening systems
Deliverables: CAD drawings, stress analysis, compliance report
Target: Q2 2026
Standards: EN 13674-1, EN 13481, UIC 719
Notes: Must comply with DB Netz AG specifications
\`\`\`

**How it helps:**
- e-pilot reads this context on every message \u2014 it knows what your project is about
- The **Analytics** tab (inside the project view) shows your objectives alongside milestone progress
- Ask e-pilot: "Are we on track to meet our project goals?" and it will answer with context`,
      },
    ],
  },
  {
    id: 'chat',
    title: 'e-pilot Chat Assistant',
    icon: 'AutoAwesome',
    articles: [
      {
        title: 'Getting Started with e-pilot',
        tourId: 'chat',
        content: `e-pilot is your AI engineering assistant, always available in the right panel of the platform.

**Three Modes:**
- **General** \u2014 Ask questions, analyze files, get engineering recommendations
- **CAD** \u2014 Generate CAD drawings and 3D models
- **Orchestrator** \u2014 Get guidance on building software pipelines

**Switching Modes:**
- Toggle **CAD mode** using the switch in the chat header (it also auto-enables when you open Design / CAD)
- **Orchestrator mode** activates automatically when you open the Orchestrator section
- Switch back to General mode by toggling CAD off

**Suggested Questions:**
When the chat is empty, e-pilot shows suggested questions for the current mode. Click any suggestion to send it instantly.

**Keyboard Shortcuts:**
- **Enter** \u2014 Send your message
- **Shift + Enter** \u2014 Add a new line without sending

**Chat History:**
Your conversations are saved automatically and persist across browser sessions. Click the **trash icon** next to the input field to clear the chat and start fresh.`,
      },
      {
        title: 'Document Q&A \u2014 Ask About Your Files',
        content: `Attach project files to your chat and ask questions about them \u2014 similar to NotebookLM but for engineering documents.

**Attaching Files:**
1. Look at the **Context Files** panel below the chat input
2. Click on any file to select it (selected files are highlighted in blue)
3. You can select multiple files at once
4. Selected files are sent as context with your next message

**Filtering Files:**
Use the **File Type** dropdown to narrow the list:
- CAD / Design
- Electronics / PCB
- Simulation / FEA
- Requirements
- Documentation
- Report
- Image

**Searching Files:**
Type a keyword in the **Search** bar and press Enter or click **Search**. This uses semantic search to find files by content, not just file name.

**Asking Questions:**
With files attached, ask anything:
- "Summarize this document"
- "What are the key specifications?"
- "List all safety requirements from the attached spec"
- "Compare these two files and list differences"

**Citations:**
e-pilot's answers include **source citations** \u2014 colored chips at the bottom of the response showing which file was referenced and a relevance percentage. This lets you verify every answer against the original source.

**Follow-up Questions:**
After asking about a document, e-pilot remembers the context. You can ask follow-up questions without re-attaching the same file.

**Supported File Types:**
PDF, DOCX, XLSX, XLS, TXT, CSV, Markdown, JSON, and code files (Python, JavaScript, HTML, CSS).`,
      },
      {
        title: 'CAD Mode \u2014 Generating Drawings',
        content: `Use e-pilot to generate 2D CAD drawings and 3D models from natural language descriptions.

**Getting Started:**
1. Select or create a project first (CAD mode requires an active project)
2. Enable **CAD mode** using the toggle in the chat header
3. Describe what you want to draw

**2D Drawings:**
Describe the drawing in plain language:
- "Draw a steel baseplate 500x300mm with 4 bolt holes"
- "Create a UIC60 rail cross-section profile"
- "Draw a rectangular flange with 6 M12 mounting holes on a 200mm bolt circle"

The agent generates a DXF file with proper layers, dimensions, hatching, and annotations.

**3D Models:**
Say "3D" explicitly in your prompt:
- "Create a 3D flange model with 6 mounting holes"
- "Generate a 3D pipe fitting assembly"

**Railway Engineering Knowledge Base:**
e-pilot includes built-in knowledge of railway components:
- 25 rail profiles (UIC60, 60E1, S49, etc.)
- 16 fastening systems
- 15 turnout geometries
- Track gauge standards (UIC, JIS, GOST, GB, IRS, BS)
- 7 assembly templates for common drawing types

**After Generation:**
- The drawing appears automatically in the CAD Viewer and file list
- A green notification confirms the drawing was created
- You can immediately view it or download it

**How It Works:**
The AI Drawing Agent uses an iterative process \u2014 it generates the drawing, validates the output (checking entity counts, bounding boxes, and correctness), and retries if needed. This self-correction ensures higher accuracy.`,
      },
      {
        title: 'Knowledge Base \u2014 Auto-Context for e-pilot',
        tourId: 'knowledge-base',
        content: `The **Knowledge Base** lets you pin documents, CAD files, requirements, and compliance standards to a project so e-pilot automatically uses them as context when answering your questions.

**Where to find it:**
1. Go to **Projects** and select a project
2. Click the **Knowledge Base** tab (third tab, next to Analytics)

**Four tabs for different content types:**
- **Documents** \u2014 PDFs, Word docs, spreadsheets, images
- **CAD** \u2014 DWG, DXF, STEP, IFC and other CAD formats
- **Requirements** \u2014 Requirement documents with attached files
- **Compliance** \u2014 Compliance standards with attached files

**Uploading files:**
1. Click **Upload** or **drag and drop** files directly onto the tab
2. Multiple files upload **in parallel** (up to 3 at a time)
3. Text extraction and indexing happen **in the background** \u2014 you can continue working
4. Files are automatically created in their respective views (Documents, CAD, Requirements, Compliance) and pinned to the KB

**Adding existing documents:**
1. Click **Add Existing**
2. A picker dialog shows available items not yet pinned to the KB
3. Check the items you want to pin, then click **Add**

**Status indicators:**
- **Ready** \u2014 Document is indexed and searchable by e-pilot
- **Indexing** \u2014 Document is being processed (automatic, takes a few seconds)
- **Failed** \u2014 Indexing failed; try removing and re-adding the document

**How it works:**
- When you ask e-pilot a question, it automatically searches your pinned Knowledge Base documents for relevant context
- Answers cite KB sources with **[KB Ref N]** labels so you can trace back to the original document
- You do not need to manually attach files \u2014 KB docs are always available as context
- All KB-uploaded files also appear in the e-pilot **context file picker** for manual attachment

**Removing documents:**
Click the delete icon next to any pinned document to remove it from the Knowledge Base. This permanently deletes the underlying file.`,
      },
      {
        title: 'Orchestrator Mode',
        content: `When you open the **Orchestrator** section, e-pilot automatically switches to Orchestrator mode \u2014 a specialized mode for building and planning software pipelines.

**What You Can Ask:**
- "Generate an execution plan for my pipeline"
- "What tools should I connect for stress analysis?"
- "Explain the data flow between AutoCAD and ANSYS"
- "Which simulation tools work best for thermal analysis?"

**How It Works:**
- e-pilot sees your pipeline canvas and understands the tool connections you have built
- It provides guidance on tool integration, data formats, and workflow optimization
- Use it alongside the visual pipeline builder to plan and validate your engineering workflows

Orchestrator mode deactivates automatically when you navigate away from the Orchestrator section.`,
      },
    ],
  },
  {
    id: 'orchestrator',
    title: 'Orchestrator',
    icon: 'AccountTree',
    articles: [
      {
        title: 'Pipeline Builder Overview',
        tourId: 'orchestrator',
        content: `The Orchestrator is a visual pipeline builder for connecting engineering software tools:

1. Navigate to **Orchestrator** in the sidebar
2. Drag software tiles from the **Software Dock** on the left onto the canvas
3. Connect tools by dragging from one node's output handle to another's input handle
4. Click **Generate Plan** to have AI create an execution plan for your pipeline
5. Review and run the generated plan

The pipeline visually represents how data flows between your engineering tools (e.g., CAD \u2192 Simulation \u2192 Report).`,
      },
      {
        title: 'Software Selection',
        content: `Personalize your Software Dock by selecting which engineering tools you work with:

1. On your first visit, a **selection modal** appears with all available tools grouped by category
2. Click tools to toggle them \u2014 selected tools show **"In dock"** (green), deselected show **"Hidden"** (faded)
3. Use **Select All** or **Clear** per category for quick changes
4. Click **Save & Continue** to apply your selection
5. Only your selected tools appear in the dock and the sidebar navigation dropdown

**To change your selection later:**
- Click the **gear icon** next to "Software Dock" in the Orchestrator view
- Your preferences are saved to your account and persist across devices`,
      },
      {
        title: 'Supported Engineering Tools',
        content: `The Orchestrator supports 14 engineering tools across 4 categories:

**CAD / Design:**
- AutoCAD, SolidWorks, Inventor, Siemens NX, Fusion 360, Onshape

**EDA / Electronics:**
- Altium Designer, KiCad

**CAE / Simulation:**
- ANSYS, Abaqus, MATLAB, OpenFOAM, COMSOL

**Visualization:**
- Rhino

Tools marked as **"Active"** have full integration. Tools marked **"Coming Soon"** are planned for future releases.`,
      },
    ],
  },
  {
    id: 'analytics',
    title: 'Project Analytics',
    icon: 'Assessment',
    articles: [
      {
        title: 'AI Health Summary',
        content: `The **AI Health Summary** is the first tab you see when opening Analytics. It uses AI to generate a plain-language overview of your project\u2019s status.

**What it does:**
- Analyzes your project data (requirements, documents, CAD drawings, milestones, version control activity)
- Generates a structured summary with sections like Executive Summary, Progress, Risks, and Recommendations
- Highlights what\u2019s on track, what needs attention, and suggested next steps

**How to use:**
1. Select a project, then click **Analytics** in the sidebar
2. The AI Health Summary loads as the default first tab
3. Click **Refresh** to regenerate with the latest data

**Best for:**
- Preparing for stakeholder updates or gate reviews
- Getting a quick overview when returning to a project after time away
- Identifying blind spots you may have missed in manual reviews`,
      },
      {
        title: 'Project Brief',
        content: `The **Project Brief** tab combines the project scorecard with KPI summaries, milestone progress, and coverage metrics.

**Scorecard (top-left):**
- A **donut gauge** showing your overall project health score (0\u2013100) with a letter grade (A through F)
- Six categories are weighted: Requirements (25%), Documents (20%), CAD Drawings (15%), Version Control (15%), Milestones (15%), Compliance (10%)
- Click any **category card** below the gauge to expand and see individual rule scores with pass/fail indicators

**KPI Cards:**
- **Requirements** \u2014 Total count with status breakdown (Open, Verified, Failed)
- **Documents** \u2014 Total count with status breakdown (draft, published)
- **CAD Drawings** \u2014 Total count with status (ready, translating, error)

**Milestone Progress:**
Shows a progress bar with the average completion across all milestones. Each milestone is listed with its current progress percentage and status. Overdue milestones (past target date, not completed) are highlighted in red.

**Coverage Summary:**
Three progress bars showing how close you are to done:
- Requirements Verified \u2014 % of requirements with "Verified" status
- Documents Released \u2014 % of documents with "published" status
- CAD Drawings Ready \u2014 % of drawings with "ready" status

**How to access:**
1. Go to **Projects** and select a project
2. Click the **Analytics** tab \u2192 **Project Brief** sub-tab`,
      },
      {
        title: 'Intelligence & Progression',
        content: `The **Intelligence** tab combines week-over-week metric trends with time-series progression charts.

**Metric Cards (top section):**
- **Commit Velocity** \u2014 Commits this week vs last week
- **Document Updates** \u2014 New documents uploaded
- **Requirements Added** \u2014 New requirements created
- **Requirements Verified** \u2014 Requirements moved to Verified status
- **Total Activity** \u2014 Overall platform actions

Each card shows the current value, a trend arrow (green up / red down / grey stable), and the percentage change. A **Key Insights** section below highlights what\u2019s improving and declining.

**Progression Charts (below divider):**
- **Commits per Week** \u2014 Bar chart of commit activity
- **Document Versions per Week** \u2014 Bar chart of file version uploads
- **Requirements Created per Week** \u2014 Stacked bar chart by status
- **Daily Activity** \u2014 Line chart of actions per day (last 30 days)

Use the **Time Range** dropdown to adjust: 4, 8, 12, 26, or 52 weeks.

**What to look for:**
- **Flat lines** \u2014 No activity, project may be stalled
- **Spikes** \u2014 Burst of work before a milestone or review
- **Declining trend** \u2014 Activity dropping, may need attention`,
      },
      {
        title: 'Risks & Attention',
        content: `The **Risks & Attention** tab flags items that may need your intervention.

**Five risk categories:**
- **Failed Requirements** \u2014 Requirements with "Failed" status that haven\u2019t been resolved
- **Stale Branches** \u2014 Branches with no commits in 14+ days (excluding main)
- **Stuck Documents** \u2014 Documents in "draft" status for 30+ days with no updates
- **Open Merge Requests** \u2014 Merge requests that have been open for 7+ days
- **Open Change Requests** \u2014 ECRs in draft status awaiting action

Each section shows a count badge and expands to a table with item details.

**All clear:**
If no risks are detected, the tab shows a green "No risks detected \u2014 everything looks healthy" message.

**When to check:**
Review the Risks tab at least weekly, or before milestone reviews and gate meetings.`,
      },
      {
        title: 'AI Health Summary',
        content: `The **AI Health Summary** tab uses AI to generate a plain-language overview of your project\u2019s status.

**What it does:**
- Analyzes your project data (requirements, documents, CAD drawings, milestones, version control activity)
- Generates a structured summary with sections like Executive Summary, Progress, Risks, and Recommendations
- Highlights what\u2019s on track, what needs attention, and suggested next steps

**How to use:**
1. Select a project, then click **Analytics** in the sidebar
2. Click the **AI Health Summary** sub-tab
3. The summary generates automatically on first visit
4. Click **Refresh** to regenerate with the latest data

**Best for:**
- Preparing for stakeholder updates or gate reviews
- Getting a quick overview when returning to a project after time away
- Identifying blind spots you may have missed in manual reviews`,
      },
    ],
  },
  {
    id: 'compliance',
    title: 'Compliance',
    icon: 'VerifiedUser',
    articles: [
      {
        title: 'Managing Compliance Standards',
        tourId: 'compliance',
        content: `The **Standards** tab lets you manage compliance standards that your project must satisfy.

1. Navigate to **Compliance** in the sidebar
2. The **Standards** tab is selected by default
3. Click **Add Standard** to create a new compliance standard

**Creating a standard:**
- Enter a **title** and **description**
- Select a **verification method**: Test, Analysis, Inspection, or Demonstration
- Set the **status**: Open, Verified, Failed, or Waived
- Attach a document (PDF, Word, Excel, images) \u2014 required on creation

**Filtering & search:**
- Filter by status using the dropdown (All, Open, Verified, Failed, Waived)
- Search by title or description using the search bar

**Editing & deleting:**
- Click the **edit icon** on any standard to update its details
- Click the **delete icon** to remove a standard (with confirmation)
- Click the attachment filename to download the attached document`,
      },
      {
        title: 'Traceability Matrix',
        content: `The **Traceability Matrix** tab provides a requirements \u00d7 drawings grid showing compliance status at a glance.

**Coverage summary bar (top):**
- Shows total requirements + standards, how many are verified, how many are unchecked
- Coverage percentage with color coding: green (>80%), yellow (50\u201380%), red (<50%)

**Matrix grid:**
- **Rows** = requirements (blue REQ badge) and compliance standards (orange STD badge)
- **Columns** = CAD drawings in the project
- **Cells** show verdict status:
  - Green circle = Pass
  - Red circle = Fail
  - Orange circle = Warning
  - Grey circle = N/A
  - Dashed circle = Assigned but not yet checked
  - Amber icon = Stale (drawing was modified since last check)
- **Click any cell** to see detailed findings, evidence, recommendations, and confidence score

**Filters:**
- Filter by verdict status (Pass, Fail, Warning, Unchecked, Stale)
- Filter by type (Requirements only, Standards only)

**Export:**
Click **Export Excel** to download the full matrix as a spreadsheet with three sheets: Matrix Grid, Coverage Summary, and Detailed Verdicts.

**How items appear in the matrix:**
1. Assign requirements/standards to a drawing in the **Dashboard** tab (\u201cChecked Against\u201d column)
2. Assigned items appear as \u201cunchecked\u201d (dashed circles) in the matrix
3. Run an AI compliance check from **Design / CAD** \u2192 drawing detail \u2192 Compliance tab
4. Matrix updates with AI verdicts (pass/fail/warning)
5. Optionally override manually in the Dashboard tab \u2014 overrides reflect in the matrix`,
      },
      {
        title: 'Compliance Dashboard',
        content: `The **Dashboard** tab shows aggregated compliance check results across all CAD drawings in your project.

**Summary cards (top row):**
- **Total Drawings** \u2014 Number of CAD drawings in the project
- **Checked** \u2014 Drawings that have been compliance-checked
- **Passed** \u2014 Drawings where all checks passed
- **Failed / Warnings** \u2014 Drawings with failed or warning verdicts

**Drawings table:**
Shows each drawing with its compliance status. Expand a row to see the detailed verdict breakdown:
- Requirement/standard ID
- Verdict (Pass, Fail, Warning, Not Applicable)
- Findings and recommendations from the AI analysis
- Confidence score

**Assigning requirements/standards to drawings:**
Click the **Checked Against** column on any drawing to assign which requirements and standards it should be verified against. This creates entries in the Traceability Matrix so you can track coverage.

**Manual override:**
Use the **Manual Status** dropdown to override the AI verdict (e.g., mark a drawing as Pass after manual review). Overrides are tracked in the Audit Log.`,
      },
      {
        title: 'Running AI Compliance Checks',
        content: `AI compliance checking analyzes a CAD drawing against your requirements and compliance standards using advanced AI.

**How to run a check:**
1. Navigate to **Design / CAD** and open a drawing's **detail drawer** (click the info icon)
2. Switch to the **Compliance** tab in the detail drawer
3. Click **Select Requirements/Standards** to choose what to check against:
   - **Requirements** (blue section) \u2014 project requirements with attached files
   - **Compliance Standards** (orange section) \u2014 from the Compliance > Standards tab
4. Click **Run Compliance Check**
5. The AI streams results in real-time, showing progress for each item being checked

**Understanding verdicts:**
- **Pass** (green) \u2014 Drawing satisfies the requirement/standard
- **Fail** (red) \u2014 Drawing does not satisfy the requirement/standard
- **Warning** (yellow) \u2014 Potential issue, needs review
- **Not Applicable** (grey) \u2014 Requirement does not apply to this drawing

Each verdict includes:
- **Findings** \u2014 What the AI observed
- **Recommendations** \u2014 Suggested actions
- **Confidence score** \u2014 How confident the AI is in the verdict
- **Source** column \u2014 REQ (requirement) or STD (compliance standard)`,
      },
      {
        title: 'Exporting Compliance Results',
        content: `After running a compliance check, you can export the results:

**From the CAD detail drawer (Compliance tab):**
- Click **Export Excel** to download results as an Excel spreadsheet
- Click **Export PDF** to download a formatted PDF report

**Results are saved automatically:**
- Compliance check results are stored on the drawing record
- Re-opening the Compliance tab shows the last check results
- The Compliance Dashboard (Compliance > Dashboard tab) aggregates results across all drawings

**Best practices:**
- Run compliance checks after significant design changes
- Export results before design reviews or gate meetings
- Use the dashboard to track which drawings still need checking`,
      },
      {
        title: 'Compliance Audit Log',
        content: `The **Audit Log** tab provides a complete history of all compliance check runs across the project.

**What it shows:**
Each row represents a single compliance check run with:
- **Drawing name** \u2014 which drawing was checked
- **Checked by** \u2014 who ran the check (or \u201cManual override\u201d)
- **Date** \u2014 when the check was run
- **Results** \u2014 summary chips showing pass/fail/warning counts

**Expanding a row** shows:
- Overall summary from the AI analysis
- Standards detected in the drawing (e.g., EN 13674-1, ISO 9001)
- Run ID for reference

**What gets logged:**
- AI compliance checks (run from Design / CAD \u2192 Compliance tab)
- Manual assignments (assigning requirements/standards to a drawing in the Dashboard)
- Manual status overrides (setting pass/fail/warning manually in the Dashboard)

**Why this matters:**
- Full audit trail for quality management (ISO 9001, AS9100)
- Track compliance verification activity over time
- Know who checked what, when, and what the results were
- Required for design reviews and regulatory audits`,
      },
    ],
  },
  {
    id: 'verification',
    title: 'Verification',
    icon: 'Checklist',
    articles: [
      {
        title: 'AI-Powered Verification Overview',
        content: `The **Verification** section provides AI-powered verification and validation tools. It has 4 tabs:

1. **Design Gates** \u2014 AI reviews requirements against design criteria. Gate checklists with approve/reset and progress tracking.
2. **Test Management** \u2014 AI generates test cases from requirements with structured steps, expected results, and execution tracking.
3. **Inspection Checklists** \u2014 AI generates categorized inspection items from CAD drawings.
4. **Verification Matrix** \u2014 Auto-maps requirements to test cases with coverage gap analysis.

All AI operations run asynchronously \u2014 results appear in the Command Center Processing Queue and auto-update when complete.`,
      },
      {
        title: 'AI Test Case Generation',
        content: `Generate test cases automatically from requirements:

1. Go to **Verification** \u2192 **Test Management** tab
2. Click **AI Generate**
3. Select requirements to generate tests for
4. The AI creates structured test cases with:
   - Test steps and expected results
   - Priority and type classification
   - Linked requirement traceability
5. Review and edit generated tests before saving

Each test case can be tracked through statuses: Draft, Ready, In Progress, Passed, Failed, Blocked.`,
      },
      {
        title: 'Inspection Checklists',
        content: `Generate inspection checklists from CAD drawings:

1. Go to **Verification** \u2192 **Inspection Checklists** tab
2. Select a CAD drawing
3. Click **Generate Checklist**
4. The AI analyzes the drawing and creates categorized inspection items covering:
   - Dimensional checks
   - Material verification
   - Surface finish requirements
   - Assembly verification points

Items are grouped by category for structured inspection workflows.`,
      },
      {
        title: 'Verification Matrix',
        content: `The Verification Matrix auto-maps requirements to test cases:

1. Go to **Verification** \u2192 **Verification Matrix** tab
2. The matrix shows which requirements have linked test cases and which have gaps
3. **Green cells** = requirement covered by passing tests
4. **Red cells** = requirement has failing tests
5. **Empty cells** = coverage gap (no test case linked)

Use this to identify untested requirements and ensure full V&V coverage before design reviews.`,
      },
    ],
  },
  {
    id: 'knowledge-base',
    title: 'Knowledge Base',
    icon: 'MenuBook',
    articles: [
      {
        title: 'Knowledge Base Overview',
        content: `The **Knowledge Base** is where you manage permanent context for e-pilot. Files pinned here are automatically searched during every e-pilot conversation.

**4 source tabs:** Documents, CAD, Requirements, Compliance \u2014 each showing pinned files of that type.

**Key features:**
- Files are auto-pinned to KB when created from any view
- **Purple sparkle icon** = file is in e-pilot context (active)
- **Grey icon** = file is stored but not in context (click to toggle)
- Search bar filters across all tabs
- Status badges show indexing progress (Ready, Indexing, Failed, Pending)`,
      },
      {
        title: 'Context Toggle',
        content: `Control which KB files e-pilot uses:

- Click the **sparkle icon** next to any file to toggle its context status
- **Enabled** (purple) \u2014 file content is searched during conversations
- **Disabled** (grey) \u2014 file is stored but not included in AI context

This lets you keep files in the KB for reference without cluttering e-pilot's context. Useful when you have many files but want the AI to focus on specific ones.`,
      },
      {
        title: 'Recently Deleted',
        content: `The **Recently Deleted** tab shows all soft-deleted items across your project:

- Documents, requirements, CAD drawings, and compliance standards that were deleted appear here
- Click **Restore** to bring an item back \u2014 it reappears in both the KB and its original view
- Click **Delete** to permanently remove an item and all its associated data (versions, compliance verdicts, search chunks)
- Permanent deletion cannot be undone

Soft-deleted items are automatically excluded from e-pilot context and all list views.`,
      },
    ],
  },
  {
    id: 'decisions',
    title: 'Decision Journal',
    icon: 'Lightbulb',
    articles: [
      {
        title: 'Engineering Decisions',
        content: `The **Decision Journal** captures the *why* behind engineering actions. Unlike the activity feed (what happened), decisions record rationale, trade-offs, and context.

**How decisions are created:**
- **Automatically** \u2014 compliance checks with failures/warnings create decision entries
- **By e-pilot** \u2014 when you make a trade-off in conversation, e-pilot records it
- **Manually** \u2014 via the API (frontend creation coming soon)

**Viewing decisions:**
1. Go to **Project Home** \u2192 **General** tab \u2192 **Activity Feed**
2. Select the **Decisions** filter from the dropdown
3. Decisions show type (design, compliance, material, risk), rationale, linked entities, and tags

**Timeline view:**
Select the **Relationship Timeline** filter to see how entity relationships changed over time \u2014 when links were created, invalidated, or superseded.`,
      },
    ],
  },
];
